import { QueryClient } from '@tanstack/react-query';

import {
  ApplyToProjectRequest,
  ApplyToProjectResponse,
  DeclineProjectRequest,
  DeclineProjectResponse,
  GetAllProjectsRequest,
  GetProjectRequest,
  GetProjectResponse,
  GetProjectsRequest,
  GetProjectsResponse,
} from '@internal/types/api';
import { Role } from '@internal/types/profile';
import { Project } from '@internal/types/projects';

import {
  httpClient,
  mutationEndpoint,
  NextContext,
  queryEndpoint,
} from '../system';

const isPartnerManager = (role?: Role) => role === 'partner manager';

export const PROJECTS_LIST_QUERY_KEY = 'projects-list';
export const PROJECT_QUERY_KEY = 'project';

export const invalidateProjectQueries = (
  queryClient: QueryClient,
  dealId?: string
): void => {
  queryClient
    .invalidateQueries({ queryKey: [PROJECTS_LIST_QUERY_KEY] })
    .catch(() => {});
  queryClient
    .invalidateQueries({
      queryKey: dealId ? [PROJECT_QUERY_KEY, dealId] : [PROJECT_QUERY_KEY],
    })
    .catch(() => {});
};

interface CrmErrorResponse {
  response?: { data?: { error?: { message?: string } } };
}

export const getProjectActionErrorMessage = (
  error: unknown,
  fallback: string
): string =>
  (
    error as CrmErrorResponse | undefined
  )?.response?.data?.error?.message?.trim() || fallback;

type ProjectsPageRequest = GetProjectsRequest & { apiKey: string };

const fetchProjectsPage = (
  { type, page, perPage, role, apiKey }: ProjectsPageRequest,
  nextContext?: NextContext
) =>
  httpClient.auto<GetProjectsResponse>({
    url: isPartnerManager(role)
      ? '/api/projects/partner-manager'
      : '/api/projects',
    method: 'POST',
    data: {
      filters: {
        // TODO: enable filters when there will be enough projects
        // specializations,
        // skills,
        // min_rate_per_hour: minRatePerHour,
        // max_rate_per_hour: maxRatePerHour,
        // projectInvolvementType: availabilityType,
      },
      pagination: {
        page,
        perPage,
      },
      sorting: {
        dateProjectCreated: 'DESC',
      },
      type,
    },
    headers: {
      'auth-key': apiKey,
    },
    nextContext,
  });

const getProjects = queryEndpoint({
  entity: 'projectsList',
  queryKey: ({ type, page, perPage }: ProjectsPageRequest) => [
    PROJECTS_LIST_QUERY_KEY,
    type,
    page,
    perPage,
  ],
  queryFn: fetchProjectsPage,
  defaultValue: { projects: [], total: 0 },
});

const ALL_PROJECTS_PAGE_SIZE = 100;

const getAllProjects = queryEndpoint({
  entity: 'projectsList',
  queryKey: ({ type }: GetAllProjectsRequest & { apiKey: string }) => [
    PROJECTS_LIST_QUERY_KEY,
    type,
    'all',
  ],
  queryFn: async (request, nextContext?) => {
    const projects: Project[] = [];
    let total = 0;
    for (let page = 1; ; page += 1) {
      const result = await fetchProjectsPage(
        { ...request, page, perPage: ALL_PROJECTS_PAGE_SIZE },
        nextContext
      );
      projects.push(...result.projects);
      total = result.total;
      if (result.projects.length === 0 || projects.length >= total) break;
    }
    return { projects, total };
  },
  defaultValue: { projects: [], total: 0 },
});

const getProject = queryEndpoint({
  entity: 'project',
  queryKey: ({ dealId, developerId, role }: GetProjectRequest) => [
    PROJECT_QUERY_KEY,
    dealId,
    ...(isPartnerManager(role) && developerId ? [developerId] : []),
  ],
  queryFn: async ({ dealId, developerId, role }, nextContext?) =>
    httpClient.auto<GetProjectResponse>({
      url:
        developerId && isPartnerManager(role)
          ? `/api/projects/partner-manager/${dealId}/${developerId}`
          : `/api/projects/${dealId}`,
      method: 'GET',
      nextContext,
    }),
  defaultValue: null,
  nullable: true,
});

const applyToProject = mutationEndpoint({
  actionName: 'applyToProject',
  mutationKey: ['apply-to-project'],
  mutationFn: async ({
    apiKey,
    dealId: _dealId,
    ...payload
  }: ApplyToProjectRequest & { apiKey: string; dealId?: string }) =>
    httpClient.auto<ApplyToProjectResponse>({
      url: '/api/projects/apply',
      method: 'PATCH',
      data: payload,
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: (queryClient, { dealId }) => {
    invalidateProjectQueries(queryClient, dealId);
    queryClient
      .invalidateQueries({ queryKey: ['candidate-roles'] })
      .catch(() => {});
  },
});

const declineProject = mutationEndpoint({
  actionName: 'declineProject',
  mutationKey: ['decline-project'],
  mutationFn: async ({
    apiKey,
    dealId: _dealId,
    ...payload
  }: DeclineProjectRequest & { apiKey: string; dealId?: string }) =>
    httpClient.auto<DeclineProjectResponse>({
      url: '/api/projects/decline',
      method: 'PATCH',
      data: payload,
      headers: {
        'auth-key': apiKey,
      },
    }),
  onSuccess: (queryClient, { dealId }) => {
    invalidateProjectQueries(queryClient, dealId);
  },
});

export {
  applyToProject,
  declineProject,
  getAllProjects,
  getProject,
  getProjects,
};
